import React, { useState, useEffect, useRef } from 'react';

const CHAPTERS = [
  {
    img: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4cdae0ca-9c75-4bc0-a101-39471645aaff/artifacts/0iynbogo_43B78FAA-A7A8-4406-8D98-E3682FEF4CA8.jpeg',
    label: 'EL RESULTADO',
    title: 'Agua pura. Siempre lista.',
    text: 'Fría o caliente. pH entre 7.0 y 8.5. Sin garrafones, sin dependencias. Solo pureza.',
  },
  {
    img: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4cdae0ca-9c75-4bc0-a101-39471645aaff/artifacts/jwsb3fj7_3CC9A703-5FAE-4776-BDB8-A886C7537DCF.jpeg',
    label: 'CONDENSACIÓN',
    title: 'Captura del aire.',
    text: 'Los ventiladores extraen humedad atmosférica y la conducen al sistema de condensación.',
  },
  {
    img: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4cdae0ca-9c75-4bc0-a101-39471645aaff/artifacts/wu6mwkko_E2E9377F-BB33-47FF-8469-1AFEADEAC76F.jpeg',
    label: 'MINERALIZACIÓN',
    title: 'Mineralización a tu gusto.',
    text: 'Las esferas minerales enriquecen el agua con los minerales exactos que eliges.',
  },
  {
    img: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4cdae0ca-9c75-4bc0-a101-39471645aaff/artifacts/ucjiyre4_070C11E2-BF49-489D-8B9C-218DA96E9984.jpeg',
    label: 'FILTRACIÓN',
    title: 'Filtración profunda.',
    text: 'El filtro de carbón activado elimina impurezas y contaminantes a nivel molecular.',
  },
];

const AnatomySection = () => {
  const containerRef = useRef(null);
  const chapterRef = useRef(0);
  const transitioningRef = useRef(false);
  const [progress, setProgress] = useState(0);
  const [displayChapter, setDisplayChapter] = useState(0);
  const [textState, setTextState] = useState('visible'); // 'visible' | 'exiting'
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (isMobile) return;

    const handleScroll = () => {
      if (!containerRef.current) return;
      const container = containerRef.current;
      const containerTop = container.getBoundingClientRect().top + window.scrollY;
      const containerHeight = container.offsetHeight;
      const windowHeight = window.innerHeight;
      const prog = Math.max(0, Math.min(1,
        (window.scrollY - containerTop) / (containerHeight - windowHeight)
      ));
      setProgress(prog);

      const newChapter = Math.min(3, Math.floor(prog * 4));
      if (newChapter !== chapterRef.current && !transitioningRef.current) {
        transitioningRef.current = true;
        chapterRef.current = newChapter;
        setTextState('exiting');
        setTimeout(() => {
          setDisplayChapter(newChapter);
          setTextState('visible');
          setTimeout(() => { transitioningRef.current = false; }, 1600);
        }, 800);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  const textStyle = {
    opacity: textState === 'exiting' ? 0 : 1,
    transform: textState === 'exiting' ? 'translateY(-24px)' : 'translateY(0)',
    transition: textState === 'exiting'
      ? 'opacity 800ms ease, transform 800ms ease'
      : 'opacity 1500ms ease, transform 1500ms ease',
    willChange: 'transform',
  };

  if (isMobile) {
    return (
      <section id="tecnologia" data-testid="anatomy-mobile" style={{ background: '#F5F5F7' }}>
        {CHAPTERS.map((ch, i) => (
          <MobileChapter key={i} chapter={ch} />
        ))}
      </section>
    );
  }

  return (
    <section
      id="tecnologia"
      ref={containerRef}
      data-testid="anatomy-section"
      style={{ height: '500vh', position: 'relative', background: '#F5F5F7' }}
    >
      <div style={{
        position: 'sticky', top: 0, height: '100vh',
        overflow: 'visible', background: '#F5F5F7',
      }}>
        {/* Full-width crossfading images */}
        {CHAPTERS.map((ch, i) => (
          <img
            key={i}
            src={ch.img}
            alt={ch.label}
            style={{
              position: 'absolute', top: 0, left: 0,
              width: '100%', height: '100%',
              objectFit: 'contain',
              mixBlendMode: 'multiply',
              opacity: displayChapter === i ? 1 : 0,
              transition: displayChapter === i
                ? 'opacity 1500ms ease-in-out'
                : 'opacity 800ms ease-in-out',
            }}
          />
        ))}

        {/* Cinematic dark gradient overlay at bottom */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: '65%',
          background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)',
          pointerEvents: 'none',
          zIndex: 1,
        }} />

        {/* Floating text over overlay */}
        <div
          style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            padding: '0 clamp(40px, 6vw, 100px) 80px',
            zIndex: 2,
            ...textStyle,
          }}
          data-testid="anatomy-text"
        >
          <div className="section-label" style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '20px' }}>
            {CHAPTERS[displayChapter].label}
          </div>
          <h2 style={{
            fontFamily: 'Inter, sans-serif', fontWeight: 700,
            fontSize: 'clamp(32px, 4vw, 64px)',
            letterSpacing: '-0.02em', color: '#FFFFFF',
            marginBottom: '20px', lineHeight: 1.05,
          }}>
            {CHAPTERS[displayChapter].title}
          </h2>
          <p style={{
            fontFamily: 'Inter, sans-serif', fontWeight: 300,
            fontSize: '17px', lineHeight: 1.75,
            letterSpacing: '0.02em', color: 'rgba(255,255,255,0.75)',
            maxWidth: '520px',
          }}>
            {CHAPTERS[displayChapter].text}
          </p>
        </div>
      </div>
    </section>
  );
};

const MobileChapter = ({ chapter }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.2 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        minHeight: '100vh',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '80px 24px',
        borderBottom: 'none',
        background: '#F5F5F7',
      }}
    >
      <div className="product-halo" style={{ width: '100%', marginBottom: '40px', background: '#F5F5F7' }}>
        <img
          src={chapter.img}
          alt={chapter.label}
          style={{
            width: '100%', height: '50vw', objectFit: 'contain',
            mixBlendMode: 'multiply',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 700ms ease-out, transform 700ms ease-out',
            willChange: 'transform',
          }}
        />
      </div>
      <div style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 700ms ease-out 200ms, transform 700ms ease-out 200ms',
        willChange: 'transform',
      }}>
        <div className="section-label" style={{ marginBottom: '16px' }}>{chapter.label}</div>
        <h2 style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '32px',
          letterSpacing: '-0.02em', color: '#0A0A0A', marginBottom: '16px',
        }}>
          {chapter.title}
        </h2>
        <p style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 300, fontSize: '16px',
          lineHeight: 1.75, color: '#6B6B6B',
        }}>
          {chapter.text}
        </p>
      </div>
    </div>
  );
};

export default AnatomySection;
